import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  decodeGroupLeaderParam,
  encodeGroupLeaderParam,
  resetGroupLeaderFilter,
  selectGroupLeader,
  setSelectedLeader,
} from "../../reducers/groupLeaderFilter";
import type { GroupLeader } from "../../services/ActionReports/ActionReportAPI.types";

const QUERY_KEY = "gl";

/**
 * Synchronizes the `groupLeaderFilter.selectedLeader` Redux slice with the
 * `?gl=<aff>-<abo>` URL query parameter.
 *
 * Behavior:
 *   - On mount: if the URL carries a `gl` value and it matches an entry in
 *     `availableLeaders`, hydrate the slice. Otherwise clear both URL + slice.
 *   - When the slice changes (user selects from dropdown or clicks clear):
 *     mirror into the URL (replace, not push, so back-button restores prior
 *     report, not prior filter state).
 *   - On route change between Action Reports (pathname changes): reset the
 *     slice and drop the param.
 *
 * First Redux↔URL sync in this repo; keep the primitive here to one feature.
 */
export const useGroupLeaderUrlSync = (availableLeaders: GroupLeader[] | undefined): void => {
  const dispatch = useAppDispatch();
  const selectedLeader = useAppSelector(selectGroupLeader);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const lastPathname = useRef<string>(location.pathname);
  const hydrated = useRef<boolean>(false);

  // Hydrate from URL once leaders arrive. If the URL references a leader
  // not in `availableLeaders`, drop the selection + param (requirement
  // "URL references a leader no longer in the response").
  useEffect(() => {
    if (hydrated.current) return;
    if (!availableLeaders) return;

    const raw = searchParams.get(QUERY_KEY);
    // Defense in depth: if a caller normalizes pre-fetch state to `[]` and a
    // `gl` deep link is present, treat the empty list as "not ready yet" so
    // we don't clear the param before the API response arrives. Callers
    // should prefer passing `undefined` while loading.
    if (raw && availableLeaders.length === 0) return;
    const decoded = decodeGroupLeaderParam(raw);
    if (!decoded) {
      // No URL selection on this report's mount. Each report's hook is a
      // fresh instance after pathname change, so the pathname-diff reset
      // below never fires across reports — clear any leader the user had
      // selected on the previous report so navigation lands on "All".
      dispatch(resetGroupLeaderFilter());
      hydrated.current = true;
      return;
    }

    const exists = availableLeaders.some((l) => l.aff === decoded.aff && l.abo === decoded.abo);
    if (exists) {
      dispatch(setSelectedLeader(decoded));
    } else {
      // Drop stale URL param. replace:true so back-button skips this URL.
      // Preserve hash (e.g. "#tree", "#inRenewal") so we don't fight the
      // tab/treeview hash-based routing.
      const next = new URLSearchParams(searchParams);
      next.delete(QUERY_KEY);
      navigate({ pathname: location.pathname, search: next.toString(), hash: location.hash }, { replace: true });
    }

    hydrated.current = true;
  }, [availableLeaders, dispatch, searchParams, navigate, location.pathname, location.hash]);

  // Mirror slice → URL whenever the selection changes. Use `navigate` (not
  // `setSearchParams`) so we explicitly carry the current hash forward —
  // setSearchParams drops the hash on some react-router versions, which
  // broke tab/treeview state on these reports.
  useEffect(() => {
    if (!hydrated.current) return;

    const encoded = encodeGroupLeaderParam(selectedLeader);
    const current = searchParams.get(QUERY_KEY) ?? "";

    if (encoded === current) return;

    const next = new URLSearchParams(searchParams);
    if (encoded) {
      next.set(QUERY_KEY, encoded);
    } else {
      next.delete(QUERY_KEY);
    }
    navigate({ pathname: location.pathname, search: next.toString(), hash: location.hash }, { replace: true });
  }, [selectedLeader, searchParams, navigate, location.pathname, location.hash]);

  // Reset on navigation between Action Reports. Pathname-only — hash and
  // search changes (tab switching, our own gl writes) must not trigger a
  // reset, otherwise selecting a leader would immediately clear itself.
  useEffect(() => {
    if (location.pathname === lastPathname.current) return;
    lastPathname.current = location.pathname;

    // Allow re-hydration against the next report's leader list.
    hydrated.current = false;
    dispatch(resetGroupLeaderFilter());

    if (searchParams.has(QUERY_KEY)) {
      const next = new URLSearchParams(searchParams);
      next.delete(QUERY_KEY);
      navigate({ pathname: location.pathname, search: next.toString(), hash: location.hash }, { replace: true });
    }
  }, [dispatch, location.pathname, location.hash, navigate, searchParams]);
};
